import { ScrapbookElementType } from 'common';
import { markupsState } from '..';

export interface DrawingState {
  isDrawing: boolean;
  currentDrawingPath: string;
  currentDrawingPageId: string | null;
  drawingStartPoint: { x: number; y: number } | null;
  isDrawingPolygon: boolean;
  polygonPoints: { x: number; y: number }[];
  polygonPageId: string | null;
}

export interface DrawingCallbacks {
  addElementToPage: (pageId: string, type: ScrapbookElementType, x: number, y: number, pathData?: string, width?: number, height?: number) => void;
  /** After a freehand stroke is saved; used so document-level pointerup can clear tool state (canvas handlers may not run). */
  onPenStrokeCommitted?: () => void;
  pausePanZoom?: () => void;
  resumePanZoom: () => void;
  getPanZoomTransform?: () => { scale: number } | null;
  getPageElement: (pageId: string) => Element | null;
  getHostElement: () => HTMLElement;
  removePolygonPreview?: () => void;
}

export class MarkupsDrawingManager {
  private state: DrawingState = {
    isDrawing: false,
    currentDrawingPath: '',
    currentDrawingPageId: null,
    drawingStartPoint: null,
    isDrawingPolygon: false,
    polygonPoints: [],
    polygonPageId: null,
  };

  private callbacks: DrawingCallbacks;

  /** When set, we hold pointer capture so pointerup/mouseup still fires after dragging outside the page. */
  private strokePointerTarget: Element | null = null;
  private strokePointerId: number | null = null;

  constructor(callbacks: DrawingCallbacks) {
    this.callbacks = callbacks;
    this.setupGlobalEventListeners();
  }

  /** Must match add/removeEventListener options so listeners are actually removed in cleanup. */
  private static readonly GLOBAL_END_CAPTURE = { capture: true };

  get isDrawing(): boolean {
    return this.state.isDrawing;
  }

  get isDrawingPolygon(): boolean {
    return this.state.isDrawingPolygon;
  }

  get polygonPoints(): { x: number; y: number }[] {
    return [...this.state.polygonPoints];
  }

  get polygonPageId(): string | null {
    return this.state.polygonPageId;
  }

  private readonly endStrokeIfDrawing = (e: Event) => {
    if (!this.state.isDrawing) {
      return;
    }
    // Ignore non-primary button releases (e.g. right-click while drawing with left).
    if (e instanceof MouseEvent && e.type === 'mouseup' && e.button !== 0) {
      return;
    }
    if (e instanceof PointerEvent && e.pointerType === 'mouse' && e.button > 0) {
      return;
    }
    this.finishDrawing();
  };

  private setupGlobalEventListeners() {
    const opts = MarkupsDrawingManager.GLOBAL_END_CAPTURE;
    // Capture phase: inner handlers (e.g. Moveable / SVG) sometimes stopPropagation on bubble,
    // so document bubble mouseup never ran and freehand never finished.
    document.addEventListener('mouseup', this.endStrokeIfDrawing, opts);
    document.addEventListener('pointerup', this.endStrokeIfDrawing, opts);
    document.addEventListener('pointercancel', this.endStrokeIfDrawing, opts);
    document.addEventListener('touchend', this.endStrokeIfDrawing, opts);
  }

  private getEventPosition(event: MouseEvent | TouchEvent): { clientX: number; clientY: number } {
    if ('touches' in event) {
      if (event.touches.length > 0) {
        return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
      }
      // touchend has empty touches — use changedTouches for the final position
      if (event.changedTouches && event.changedTouches.length > 0) {
        return { clientX: event.changedTouches[0].clientX, clientY: event.changedTouches[0].clientY };
      }
    }
    return { clientX: (event as MouseEvent).clientX, clientY: (event as MouseEvent).clientY };
  }

  private getPageContext(pageId: string): { pageElement: Element; pageX: number; pageY: number; pageBounds: DOMRect } | null {
    const pageElement = this.callbacks.getPageElement(pageId);
    if (!pageElement) {
      return null;
    }

    const pageRect = pageElement.querySelector('rect');
    const pageX = pageRect ? parseFloat(pageRect.getAttribute('x') || '0') : 0;
    const pageY = pageRect ? parseFloat(pageRect.getAttribute('y') || '0') : 0;
    const pageBounds = pageElement.getBoundingClientRect();

    return { pageElement, pageX, pageY, pageBounds };
  }

  startDrawing(event: MouseEvent | TouchEvent, pageId: string) {
    if (this.state.isDrawing) return;

    const pageContext = this.getPageContext(pageId);
    if (!pageContext) {
      return;
    }

    this.releaseStrokePointerCapture();

    this.callbacks?.pausePanZoom();
    this.state.isDrawing = true;
    this.state.currentDrawingPageId = pageId;

    const transform = this.callbacks?.getPanZoomTransform();
    const scale = transform ? transform.scale : 1;

    const { clientX, clientY } = this.getEventPosition(event);
    const svgRelativeX = (clientX - pageContext.pageBounds.left) / scale;
    const svgRelativeY = (clientY - pageContext.pageBounds.top) / scale;

    // Convert to absolute SVG coordinates by adding the page's position
    const absoluteX = pageContext.pageX + svgRelativeX;
    const absoluteY = pageContext.pageY + svgRelativeY;

    this.state.drawingStartPoint = { x: svgRelativeX, y: svgRelativeY };
    this.state.currentDrawingPath = `M ${absoluteX} ${absoluteY}`;

    if ('pointerId' in event && typeof (event as PointerEvent).pointerId === 'number') {
      const pid = (event as PointerEvent).pointerId;
      const targetEl = pageContext.pageElement;
      if (targetEl && typeof (targetEl as unknown as { setPointerCapture?: (id: number) => void }).setPointerCapture === 'function') {
        try {
          (targetEl as unknown as { setPointerCapture: (id: number) => void }).setPointerCapture(pid);
          this.strokePointerTarget = targetEl;
          this.strokePointerId = pid;
        } catch {
          // Some SVG / browser combinations reject capture; global listeners still apply.
        }
      }
    }
  }

  continueDrawing(event: MouseEvent | TouchEvent) {
    if (!this.state.isDrawing || !this.state.currentDrawingPageId) return;
    // Do not extend the stroke after the primary button has been released (mousemove can still fire).
    if (event instanceof MouseEvent && (event.buttons & 1) === 0) {
      return;
    }

    const pageContext = this.getPageContext(this.state.currentDrawingPageId);
    if (!pageContext) {
      return;
    }

    const transform = this.callbacks?.getPanZoomTransform();
    const scale = transform ? transform.scale : 1;

    const { clientX, clientY } = this.getEventPosition(event);
    const svgRelativeX = (clientX - pageContext.pageBounds.left) / scale;
    const svgRelativeY = (clientY - pageContext.pageBounds.top) / scale;

    // Convert to absolute SVG coordinates by adding the page's position
    const absoluteX = pageContext.pageX + svgRelativeX;
    const absoluteY = pageContext.pageY + svgRelativeY;

    this.state.currentDrawingPath += ` L ${absoluteX} ${absoluteY}`;

    this.updateTemporaryDrawing(this.state.currentDrawingPageId, this.state.currentDrawingPath);
  }

  finishDrawing() {
    if (!this.state.isDrawing || !this.state.currentDrawingPageId || !this.state.currentDrawingPath) return;

    this.releaseStrokePointerCapture();

    // Get page position to convert absolute coordinates back to page-relative
    const pageContext = this.getPageContext(this.state.currentDrawingPageId);
    if (!pageContext) {
      this.cancelDrawing();
      return;
    }

    const pathBounds = this.calculatePathBounds(this.state.currentDrawingPath);
    const relativePath = this.makePathRelative(this.state.currentDrawingPath, pathBounds.x, pathBounds.y);

    // Convert absolute SVG coordinates to page-relative coordinates
    const pageRelativeX = pathBounds.x - pageContext.pageX;
    const pageRelativeY = pathBounds.y - pageContext.pageY;

    this.callbacks.addElementToPage(this.state.currentDrawingPageId, ScrapbookElementType.PEN, pageRelativeX, pageRelativeY, relativePath);

    this.callbacks.onPenStrokeCommitted?.();

    this.resetDrawingState();
    this.callbacks?.resumePanZoom();
    this.removeTemporaryDrawing();
  }

  createLineElement(pageId: string, startX: number, startY: number, endX: number, endY: number): void {
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    // Normalize coordinates for proper scaling
    const relativeStartX = (startX - x) / width;
    const relativeStartY = (startY - y) / height;
    const relativeEndX = (endX - x) / width;
    const relativeEndY = (endY - y) / height;

    const pathData = `M ${relativeStartX} ${relativeStartY} L ${relativeEndX} ${relativeEndY}`;

    this.callbacks.addElementToPage(pageId, ScrapbookElementType.LINE, x, y, pathData, Math.max(width, 10), Math.max(height, 10));
  }

  createArrowElement(pageId: string, startX: number, startY: number, endX: number, endY: number): void {
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    // Normalize coordinates for proper scaling (just the line part)
    const relativeStartX = (startX - x) / width;
    const relativeStartY = (startY - y) / height;
    const relativeEndX = (endX - x) / width;
    const relativeEndY = (endY - y) / height;

    // Store just the line - arrowhead will be added at render time with fixed size
    const pathData = `M ${relativeStartX} ${relativeStartY} L ${relativeEndX} ${relativeEndY}`;

    this.callbacks.addElementToPage(pageId, ScrapbookElementType.ARROW, x, y, pathData, Math.max(width, 10), Math.max(height, 10));
  }

  startPolygonDrawing(pageId: string): void {
    this.state.isDrawingPolygon = true;
    this.state.polygonPageId = pageId;
    this.state.polygonPoints = [];
    this.callbacks?.pausePanZoom();
  }

  addPolygonPoint(x: number, y: number): void {
    if (!this.state.isDrawingPolygon || !this.state.polygonPageId) return;

    this.state.polygonPoints.push({ x, y });
    this.updatePolygonPreview();
  }

  private updatePolygonPreview(): void {
    if (!this.state.isDrawingPolygon || !this.state.polygonPageId || this.state.polygonPoints.length < 2) return;

    const pathData = this.createPolygonPathData(this.state.polygonPoints, false);
    this.updateTemporaryDrawing(this.state.polygonPageId, pathData);
  }

  private createPolygonPathData(points: { x: number; y: number }[], closed: boolean = false): string {
    if (points.length < 2) return '';

    let pathData = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      pathData += ` L ${points[i].x} ${points[i].y}`;
    }

    if (closed && points.length > 2) {
      pathData += ' Z';
    }

    return pathData;
  }

  private isPointNearFirstPoint(x: number, y: number, threshold: number = 15): boolean {
    if (this.state.polygonPoints.length < 3) return false;

    const firstPoint = this.state.polygonPoints[0];
    const distance = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));
    return distance <= threshold;
  }

  tryClosePolygon(x: number, y: number): boolean {
    if (!this.state.isDrawingPolygon || this.state.polygonPoints.length < 3) return false;

    if (this.isPointNearFirstPoint(x, y)) {
      this.finishPolygonDrawing();
      return true;
    }

    return false;
  }

  finishPolygonDrawing(): void {
    if (!this.state.isDrawingPolygon || !this.state.polygonPageId || this.state.polygonPoints.length < 3) {
      this.cancelPolygonDrawing();
      return;
    }

    // Calculate bounds for the polygon
    const minX = Math.min(...this.state.polygonPoints.map(p => p.x));
    const maxX = Math.max(...this.state.polygonPoints.map(p => p.x));
    const minY = Math.min(...this.state.polygonPoints.map(p => p.y));
    const maxY = Math.max(...this.state.polygonPoints.map(p => p.y));

    const width = maxX - minX;
    const height = maxY - minY;

    // Convert points to relative coordinates (0-1 range)
    const relativePoints = this.state.polygonPoints.map(point => ({
      x: (point.x - minX) / width,
      y: (point.y - minY) / height,
    }));

    // Create normalized path data
    const pathData = this.createPolygonPathData(relativePoints, true);

    // Convert absolute SVG coordinates to page-relative coordinates
    const pageElement = this.callbacks.getPageElement(this.state.polygonPageId);
    if (pageElement) {
      const pageRect = pageElement.querySelector('rect');
      if (pageRect) {
        const pageX = parseFloat(pageRect.getAttribute('x') || '0');
        const pageY = parseFloat(pageRect.getAttribute('y') || '0');

        const pageRelativeX = minX - pageX;
        const pageRelativeY = minY - pageY;

        this.callbacks.addElementToPage(this.state.polygonPageId, ScrapbookElementType.POLYGON, pageRelativeX, pageRelativeY, pathData, Math.max(width, 10), Math.max(height, 10));
      }
    }

    this.resetPolygonState();
  }

  cancelPolygonDrawing(): void {
    if (!this.state.isDrawingPolygon) return;

    this.resetPolygonState();
  }

  private resetPolygonState(): void {
    this.state.isDrawingPolygon = false;
    this.state.polygonPoints = [];
    this.state.polygonPageId = null;
    this.callbacks?.resumePanZoom();
    this.removeTemporaryDrawing();
    this.callbacks.removePolygonPreview?.();
  }

  cancelDrawing() {
    if (!this.state.isDrawing) return;

    this.releaseStrokePointerCapture();
    this.resetDrawingState();
    this.callbacks?.resumePanZoom();
    this.removeTemporaryDrawing();
  }

  private releaseStrokePointerCapture() {
    if (this.strokePointerTarget == null || this.strokePointerId == null) {
      return;
    }
    const el = this.strokePointerTarget as unknown as { releasePointerCapture?: (id: number) => void; hasPointerCapture?: (id: number) => boolean };
    try {
      if (typeof el.hasPointerCapture === 'function' && el.hasPointerCapture(this.strokePointerId) && typeof el.releasePointerCapture === 'function') {
        el.releasePointerCapture(this.strokePointerId);
      }
    } catch {
      // ignore
    }
    this.strokePointerTarget = null;
    this.strokePointerId = null;
  }

  private resetDrawingState() {
    this.state.isDrawing = false;
    this.state.currentDrawingPath = '';
    this.state.currentDrawingPageId = null;
    this.state.drawingStartPoint = null;
    this.resetPolygonState();
  }

  private calculatePathBounds(pathData: string): { x: number; y: number; width: number; height: number } {
    const coordinates = pathData.match(/[\d.-]+/g)?.map(Number) || [];
    if (coordinates.length < 2) return { x: 0, y: 0, width: 100, height: 100 };

    let minX = coordinates[0];
    let maxX = coordinates[0];
    let minY = coordinates[1];
    let maxY = coordinates[1];

    for (let i = 0; i < coordinates.length; i += 2) {
      const x = coordinates[i];
      const y = coordinates[i + 1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    return {
      x: minX - 5,
      y: minY - 5,
      width: maxX - minX + 10,
      height: maxY - minY + 10,
    };
  }

  private makePathRelative(pathData: string, offsetX: number, offsetY: number): string {
    return pathData.replace(/([ML])\s*([-\d.]+)\s*([-\d.]+)/g, (_match, command, x, y) => {
      const relativeX = parseFloat(x) - offsetX;
      const relativeY = parseFloat(y) - offsetY;
      return `${command} ${relativeX} ${relativeY}`;
    });
  }

  static makePathAbsolute(pathData: string, offsetX: number, offsetY: number): string {
    return pathData.replace(/([ML])\s*([-\d.]+)\s*([-\d.]+)/g, (_match, command, x, y) => {
      const absoluteX = parseFloat(x) + offsetX;
      const absoluteY = parseFloat(y) + offsetY;
      return `${command} ${absoluteX} ${absoluteY}`;
    });
  }

  updateTemporaryDrawing(pageId: string, pathData: string) {
    this.removeTemporaryDrawing();

    const hostElement = this.callbacks.getHostElement();
    const pageGroup = hostElement.querySelector(`[data-page-id="${pageId}"]`);
    if (pageGroup && pathData) {
      const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tempPath.setAttribute('d', pathData);
      tempPath.setAttribute('stroke', markupsState.currentColor);
      tempPath.setAttribute('stroke-width', '2');
      tempPath.setAttribute('fill', 'none');
      tempPath.setAttribute('class', 'temp-drawing-path');
      tempPath.style.pointerEvents = 'none';

      pageGroup.appendChild(tempPath);
    }
  }

  removeTemporaryDrawing() {
    const hostElement = this.callbacks.getHostElement();
    const tempPaths = hostElement.querySelectorAll('.temp-drawing-path');
    tempPaths.forEach(path => path.remove());
  }

  cleanup() {
    this.releaseStrokePointerCapture();
    const opts = MarkupsDrawingManager.GLOBAL_END_CAPTURE;
    document.removeEventListener('mouseup', this.endStrokeIfDrawing, opts);
    document.removeEventListener('pointerup', this.endStrokeIfDrawing, opts);
    document.removeEventListener('pointercancel', this.endStrokeIfDrawing, opts);
    document.removeEventListener('touchend', this.endStrokeIfDrawing, opts);
  }
}
